import { z } from 'zod';
/** Whether a rule's offset counts every calendar day or only business days. */
export var NotificationTimingRuleDayType;
(function (NotificationTimingRuleDayType) {
    NotificationTimingRuleDayType["BusinessDay"] = "BUSINESS_DAY";
    NotificationTimingRuleDayType["CalendarDay"] = "CALENDAR_DAY";
})(NotificationTimingRuleDayType || (NotificationTimingRuleDayType = {}));
const ruleBase = {
    validStartTime: z.iso.time(),
    validEndTime: z.iso.time(),
    dayType: z.enum(NotificationTimingRuleDayType),
};
/**
 * When to send, relative to one anchor, and the window of the day the send is allowed to land in.
 * Exactly one of the two offsets is set — the other is null, which a database check constraint
 * enforces.
 */
export const NotificationTimingRuleSchema = z.union([
    z.object({
        ...ruleBase,
        minutesBeforeEncounterStartTime: z.number(),
        minutesAfterFormSetCreated: z.null(),
    }),
    z.object({
        ...ruleBase,
        minutesBeforeEncounterStartTime: z.null(),
        minutesAfterFormSetCreated: z.number(),
    }),
]);
/**
 * One step of a schedule: a named point in time, described by one or more rules, that a notification
 * scheme then attaches content to. `previousNotificationTimingId` chains the steps into a sequence.
 */
export const NotificationTimingSchema = z.object({
    id: z.uuid(),
    created: z.iso.datetime(),
    updated: z.iso.datetime(),
    notificationScheduleId: z.uuid(),
    name: z.string(),
    previousNotificationTimingId: z.uuid().nullable(),
    deletedTimestamp: z.iso.datetime().nullable(),
    rules: z.array(NotificationTimingRuleSchema),
});
const upsertRuleBase = {
    ...ruleBase,
    dayType: z.enum(NotificationTimingRuleDayType).optional(),
};
/**
 * A rule on the way in. The two anchors are mutually exclusive, so the caller sets one and either
 * omits the other or sends it as null.
 */
export const NotificationTimingRuleUpsertSchema = z.union([
    z
        .object({
        ...upsertRuleBase,
        minutesBeforeEncounterStartTime: z.int().nonnegative(),
        minutesAfterFormSetCreated: z.null().optional(),
    })
        .strict(),
    z
        .object({
        ...upsertRuleBase,
        minutesBeforeEncounterStartTime: z.null().optional(),
        minutesAfterFormSetCreated: z.int().nonnegative(),
    })
        .strict(),
]);
/**
 * A timing on the way in. `id` identifies an existing row to update, and a timing left out of the
 * array is deleted. `timingId` / `previousTimingId` are caller-assigned labels, not row ids: they
 * describe the chain within this one request, and primary resolves them to real ids after insert.
 */
export const NotificationTimingUpsertSchema = z
    .object({
    id: z.uuid().optional(),
    name: z.string().min(1),
    rules: z.array(NotificationTimingRuleUpsertSchema).min(1),
    timingId: z.string().min(1),
    previousTimingId: z.string().min(1).nullable(),
})
    .strict();
//# sourceMappingURL=timing.js.map